/**
 * ModuleViewport — Container between TopBar and BottomNav.
 *
 * Manages module lifecycle:
 * - 'import' modules: dynamically imported, mounted into a div container
 * - 'iframe' modules: sandboxed iframe with postMessage bridge
 * - Caches mounted modules (show/hide on tab switch)
 * - Shows skeleton loader during module load (not just spinner)
 * - Fade transition between modules
 * - Preloads adjacent modules on idle (requestIdleCallback)
 * - Shows offline fallback when module requires network and user is offline
 */

import type { AweniContext } from '../context/AweniContext';
import { getModule, MODULE_REGISTRY } from '../modules/registry';
import { setTopBarModuleName } from './TopBar';
import { getParentalControlService } from '../services/ParentalControlService';

let _viewport: HTMLElement | null = null;
let _ctx: AweniContext | null = null;
let _activeModuleId: string | null = null;
let _loadingEl: HTMLElement | null = null;
let _offlineEl: HTMLElement | null = null;

// Caches for mounted modules
const _containers: Map<string, HTMLElement> = new Map();
const _moduleInstances: Map<
  string,
  { unmount: () => void; onRouteChange?: (sub: string) => void }
> = new Map();
const _iframes: Map<string, HTMLIFrameElement> = new Map();

/**
 * Mount the module viewport into the given container element.
 */
export function mountModuleViewport(container: HTMLElement, ctx: AweniContext): void {
  _viewport = container;
  _ctx = ctx;

  // Inject transition styles
  _injectViewportStyles();

  // Loading skeleton overlay (replaces plain spinner)
  const loading = document.createElement('div');
  loading.style.cssText = `
    position: absolute;
    inset: 0;
    display: none;
    flex-direction: column;
    background: var(--bg-primary);
    z-index: 10;
    padding: 16px;
  `;
  loading.innerHTML = `
    <div style="display:flex;flex-direction:column;gap:16px;max-width:480px;margin:0 auto;width:100%;padding-top:8px;">
      <div style="height:24px;width:50%;background:var(--bg-secondary);border-radius:8px;" class="vp-skeleton-pulse"></div>
      <div style="height:80px;width:100%;background:var(--bg-secondary);border-radius:16px;" class="vp-skeleton-pulse"></div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
        <div style="height:88px;background:var(--bg-secondary);border-radius:16px;" class="vp-skeleton-pulse"></div>
        <div style="height:88px;background:var(--bg-secondary);border-radius:16px;" class="vp-skeleton-pulse"></div>
      </div>
      <div style="height:60px;width:100%;background:var(--bg-secondary);border-radius:16px;" class="vp-skeleton-pulse"></div>
      <div style="height:120px;width:100%;background:var(--bg-secondary);border-radius:16px;" class="vp-skeleton-pulse"></div>
    </div>
    <div style="display:flex;flex-direction:column;align-items:center;gap:8px;margin-top:24px;">
      <div class="aweni-spinner"></div>
      <span style="font-size:14px;color:var(--text-secondary);">${ctx.i18n.t('common_loading')}</span>
    </div>
  `;
  _loadingEl = loading;
  container.appendChild(loading);

  // Offline fallback overlay — content is built dynamically on each call to
  // _showOffline() so it can branch on isOnline and display correct copy.
  const offline = document.createElement('div');
  offline.style.cssText = `
    position: absolute;
    inset: 0;
    display: none;
    align-items: center;
    justify-content: center;
    background: var(--bg-primary);
    z-index: 10;
    padding: 24px;
  `;
  const offlineContent = document.createElement('div');
  offlineContent.style.cssText = 'text-align:center;max-width:300px;';
  offline.appendChild(offlineContent);
  _offlineEl = offline;
  container.appendChild(offline);

  // NOTE: idle preload is intentionally NOT scheduled here. It used to fire
  // during shell mount, which raced with the active module's import (same
  // chunk fetched twice in parallel) and could surface as NS_BINDING_ABORTED.
  // Instead, scheduleIdlePreload() runs once after the FIRST module finishes
  // mounting — see _maybeScheduleFirstIdlePreload() below.
}

// Idle preload was used to warm adjacent module chunks during browser idle
// time. With Vite's `inlineDynamicImports: true` shipping AWENI as a single
// bundle, there are no separate chunks to preload — every module is already
// resolved by the time the page boots. The preload helper is a no-op now;
// kept as a stub so call sites stay valid, will be removed once we confirm
// the single-bundle approach is stable.
function _maybeScheduleFirstIdlePreload(): void {
  /* no-op with single bundle */
}

/**
 * Switch to a module by ID. Dynamically loads it if not cached.
 *
 * `subpath` is the slash-joined remainder after the module key in the hash
 * (e.g. for `#/games/play/math-quest` the subpath is `play/math-quest`).
 * Modules that implement `onRouteChange(subpath)` get notified after mount
 * so deep links from notifications, auth-return paths, and shared URLs land
 * on the right inner view instead of the module home.
 */
export async function setActiveModule(
  moduleId: string,
  ctx?: AweniContext,
  subpath?: string,
): Promise<void> {
  const c = ctx || _ctx;
  if (!_viewport || !c) return;

  const moduleDef = getModule(moduleId);
  if (!moduleDef) {
    console.warn(`[ModuleViewport] Unknown module: ${moduleId}`);
    return;
  }

  // Parental control enforcement (child profiles only) — skip for auth/profile
  const parentalService = getParentalControlService();
  if (
    parentalService &&
    parentalService.isEnabled &&
    moduleId !== 'home' &&
    moduleId !== 'settings' &&
    moduleId !== 'auth' &&
    moduleId !== 'profile'
  ) {
    // Check blocked modules
    if (!parentalService.canAccessModule(moduleId)) {
      _showBlockScreen(c, 'module-blocked');
      _activeModuleId = moduleId;
      setTopBarModuleName(moduleId);
      return;
    }
    // Check time limit
    if (parentalService.isTimeLimitReached()) {
      _showBlockScreen(c, 'time-expired');
      _activeModuleId = moduleId;
      setTopBarModuleName(moduleId);
      return;
    }
    // Check allowed hours
    if (!parentalService.isWithinAllowedHours()) {
      _showBlockScreen(c, 'outside-hours');
      _activeModuleId = moduleId;
      setTopBarModuleName(moduleId);
      return;
    }
  }

  // Check offline
  if (!moduleDef.offlineCapable && !c.offline.isOnline) {
    _showOffline();
    _activeModuleId = moduleId;
    setTopBarModuleName(moduleId);
    return;
  }
  _hideOffline();

  // Hide current module
  if (_activeModuleId && _activeModuleId !== moduleId) {
    _hideModule(_activeModuleId);
  }

  _activeModuleId = moduleId;
  setTopBarModuleName(moduleId);

  // Already cached — just show it
  if (moduleDef.loadStrategy === 'import' && _containers.has(moduleId)) {
    _showModule(moduleId);
    _dispatchSubpath(moduleId, subpath);
    _maybeScheduleFirstIdlePreload();
    return;
  }
  if (moduleDef.loadStrategy === 'iframe' && _iframes.has(moduleId)) {
    _showIframe(moduleId);
    _dispatchSubpath(moduleId, subpath);
    _maybeScheduleFirstIdlePreload();
    return;
  }

  // Load new module
  _showLoading();

  try {
    if (moduleDef.loadStrategy === 'import') {
      await _loadImportModule(moduleId, moduleDef.entryPoint, c);
    } else if (moduleDef.loadStrategy === 'iframe') {
      _loadIframeModule(moduleId, moduleDef.entryPoint, c);
    }
    _dispatchSubpath(moduleId, subpath);
    // First module is mounted — now safe to warm adjacent chunks during idle.
    // Doing this earlier raced with the active import and contributed to
    // NS_BINDING_ABORTED on first paint after a deploy.
    _maybeScheduleFirstIdlePreload();
  } catch (err) {
    // Detailed diagnostic for the recurring NS_BINDING_ABORTED class. Logs
    // the controller state, online flag, and SW registration so we can
    // tell from a user-pasted console log whether the import failed under
    // SW control, network offline, or a transitive chunk abort.
    const diag = {
      moduleId,
      online: navigator.onLine,
      controller: navigator.serviceWorker?.controller?.scriptURL || 'none',
      hasController: !!navigator.serviceWorker?.controller,
      err: err instanceof Error ? { name: err.name, message: err.message } : String(err),
    };
    console.error(`[ModuleViewport] Failed to load module '${moduleId}':`, err);
    console.error(`[ModuleViewport] diagnostic:`, JSON.stringify(diag));
    _ctx = c;
    _showOffline();
  } finally {
    _hideLoading();
  }
}

// Notify a module about its current subpath so it can render the correct inner
// view. Silent no-op if the module doesn't implement onRouteChange.
function _dispatchSubpath(moduleId: string, subpath?: string): void {
  if (!subpath) return;
  const inst = _moduleInstances.get(moduleId);
  if (inst?.onRouteChange) {
    try {
      inst.onRouteChange(subpath);
    } catch (err) {
      console.warn(`[ModuleViewport] onRouteChange threw for '${moduleId}':`, err);
    }
  }
}

/**
 * Dynamic import of a TypeScript module.
 * The module must export a class with mount(container, context) and unmount().
 */
async function _loadImportModule(
  moduleId: string,
  entryPoint: string,
  ctx: AweniContext,
): Promise<void> {
  if (!_viewport) return;

  const container = document.createElement('div');
  container.className = 'module-container';
  container.dataset.module = moduleId;
  container.style.cssText = 'min-height:100%;';
  _viewport.appendChild(container);
  _containers.set(moduleId, container);

  // Modules are dynamic-imported via the static map in _importFallback.
  // We do NOT use a runtime-built import path: a `@vite-ignore`'d import emits
  // a literal URL (e.g. "./modules/home/HomeModule") which the browser
  // resolves to /aweni/assets/modules/home/HomeModule — a path that doesn't
  // exist as a chunk; the SW navigation fallback then returns index.html,
  // the JS parser fails, and unrelated in-flight requests abort with
  // NS_BINDING_ABORTED. The static map gives Vite real code-split chunks.
  // entryPoint is kept on registry entries for documentation only.
  void entryPoint;
  const mod: Record<string, unknown> = await _importFallback(moduleId);

  // Find the default export or the first class/object with mount()
  const ModuleExport = mod.default || mod[Object.keys(mod)[0]];
  let instance: {
    mount: (el: HTMLElement, ctx: AweniContext) => void | Promise<void>;
    unmount: () => void;
    onRouteChange?: (s: string) => void;
  };

  if (typeof ModuleExport === 'function') {
    instance = new (ModuleExport as new () => typeof instance)();
  } else {
    instance = ModuleExport as typeof instance;
  }

  await instance.mount(container, ctx as unknown as Parameters<typeof instance.mount>[1]);
  _moduleInstances.set(moduleId, instance);
}

/**
 * Fallback dynamic import for known modules.
 */
async function _importFallback(moduleId: string): Promise<Record<string, unknown>> {
  switch (moduleId) {
    case 'home':
      return import('../modules/home/HomeModule');
    case 'games':
      return import('../modules/games/GamesAdapter');
    case 'memoriz':
      return import('../modules/memoriz/MemorizModule');
    case 'balancetab':
      return import('../modules/balancetab/BalanceTabModule');
    case 'blog':
      return import('../modules/blog/BlogModule');
    case 'chat':
      return import('../modules/chat/ChatModule');
    case 'community':
      return import('../modules/community/CommunityModule');
    case 'selecto':
      return import('../modules/selecto/SelectoAdapter');
    case 'settings':
      return import('../modules/settings/SettingsModule');
    case 'parental':
      return import('../modules/parental/ParentalDashboard');
    case 'upgrade':
      return import('../modules/upgrade/UpgradeModule');
    case 'family':
      return import('../modules/family/FamilyModule');
    case 'watch':
      return import('../modules/watch/WatchModule');
    case 'auth':
      return import('../modules/auth/AuthModule');
    case 'profile':
      return import('../modules/profile/ProfileModule');
    default:
      throw new Error(`No fallback import for module: ${moduleId}`);
  }
}

/**
 * Load an iframe-based module.
 */
function _loadIframeModule(moduleId: string, url: string, ctx: AweniContext): void {
  if (!_viewport) return;

  const iframe = document.createElement('iframe');
  iframe.src = url;
  iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-popups allow-forms');
  iframe.setAttribute('loading', 'lazy');
  iframe.setAttribute('title', ctx.i18n.t(`nav_${moduleId}`) || moduleId);
  iframe.style.cssText = 'width:100%;height:100%;border:none;';

  // PostMessage bridge for shell-iframe communication
  const messageHandler = (event: MessageEvent) => {
    if (event.source !== iframe.contentWindow) return;
    _handleIframeMessage(moduleId, event.data, ctx);
  };
  window.addEventListener('message', messageHandler);

  iframe.addEventListener('load', () => {
    // Send context to iframe
    iframe.contentWindow?.postMessage(
      {
        type: 'aweni:context',
        auth: {
          isAuthenticated: ctx.auth.isAuthenticated,
          token: ctx.auth.getToken(),
          user: ctx.auth.user,
          profile: ctx.auth.activeProfile,
        },
        theme: { isDark: ctx.theme.isDark },
        locale: ctx.i18n.locale,
      },
      '*',
    );
  });

  _viewport.appendChild(iframe);
  _iframes.set(moduleId, iframe);
}

function _handleIframeMessage(
  moduleId: string,
  data: Record<string, unknown>,
  ctx: AweniContext,
): void {
  if (!data || typeof data.type !== 'string') return;
  switch (data.type) {
    case 'aweni:navigate':
      if (typeof data.path === 'string') ctx.navigate(data.path);
      break;
    case 'aweni:trackActivity':
      if (typeof data.action === 'string')
        ctx.trackActivity(data.action, data.data as Record<string, unknown>);
      break;
    case 'aweni:requestAuth': {
      const iframe = _iframes.get(moduleId);
      iframe?.contentWindow?.postMessage(
        {
          type: 'aweni:context',
          auth: {
            isAuthenticated: ctx.auth.isAuthenticated,
            token: ctx.auth.getToken(),
            user: ctx.auth.user,
            profile: ctx.auth.activeProfile,
          },
        },
        '*',
      );
      break;
    }
  }
}

function _showModule(id: string): void {
  const el = _containers.get(id);
  if (el) {
    el.style.display = '';
    // Fade-in transition
    el.style.opacity = '0';
    el.style.transition = 'opacity 200ms ease';
    requestAnimationFrame(() => {
      el.style.opacity = '1';
    });
  }
}

function _hideModule(id: string): void {
  const c = _containers.get(id);
  if (c) {
    c.style.transition = 'opacity 150ms ease';
    c.style.opacity = '0';
    // Wait for transition then hide
    setTimeout(() => {
      c.style.display = 'none';
    }, 150);
  }
  const f = _iframes.get(id);
  if (f) f.style.display = 'none';
}

function _showIframe(id: string): void {
  const f = _iframes.get(id);
  if (f) {
    f.style.display = '';
    f.style.opacity = '0';
    f.style.transition = 'opacity 200ms ease';
    requestAnimationFrame(() => {
      f.style.opacity = '1';
    });
  }
}

// Defer the skeleton briefly so cached/instant module switches never flash it.
// 80 ms threshold = below typical perception of motion, well above same-tick cache hits.
let _loadingShowTimer: number | null = null;
const LOADING_SHOW_DELAY_MS = 80;

function _showLoading(): void {
  if (!_loadingEl) return;
  if (_loadingShowTimer !== null) return;
  _loadingShowTimer = window.setTimeout(() => {
    if (_loadingEl) _loadingEl.style.display = 'flex';
    _loadingShowTimer = null;
  }, LOADING_SHOW_DELAY_MS);
}

function _hideLoading(): void {
  if (_loadingShowTimer !== null) {
    clearTimeout(_loadingShowTimer);
    _loadingShowTimer = null;
  }
  if (_loadingEl) _loadingEl.style.display = 'none';
}

function _showOffline(): void {
  if (!_offlineEl || !_ctx) return;
  const ctx = _ctx;
  const t = ctx.i18n.t.bind(ctx.i18n);
  const isOnline = ctx.offline.isOnline;
  const locale = (ctx.i18n.locale as string) || 'en';

  const offlineContent = _offlineEl.querySelector('div') as HTMLElement;
  offlineContent.innerHTML = ''; // clear previous content

  // Icon — no-wifi SVG
  const icon = document.createElement('div');
  icon.style.cssText = 'margin:0 auto 16px;';
  icon.innerHTML = `<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="1" y1="1" x2="23" y2="23"/><path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"/><path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"/><path d="M10.71 5.05A16 16 0 0 1 22.56 9"/><path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/></svg>`;

  const title = document.createElement('h2');
  title.style.cssText =
    'font-size:18px;font-weight:600;color:var(--text-primary);margin-bottom:8px;';
  title.textContent = isOnline ? t('offline_service_down_title') : t('offline_no_connection_title');

  const msg = document.createElement('p');
  msg.style.cssText = 'font-size:14px;color:var(--text-secondary);margin-bottom:12px;';
  msg.textContent = isOnline
    ? t('offline_service_down_message')
    : t('offline_no_connection_message');

  offlineContent.append(icon, title, msg);

  // Deep-link buttons for offline-capable modules
  const offlineCapable = MODULE_REGISTRY.filter((m) => m.offlineCapable && m.id !== 'home');
  if (offlineCapable.length > 0) {
    const row = document.createElement('div');
    row.style.cssText =
      'display:flex;flex-wrap:wrap;gap:8px;justify-content:center;margin-bottom:14px;';
    offlineCapable.forEach((mod) => {
      const a = document.createElement('a');
      a.href = `#/${mod.id}`;
      a.style.cssText =
        'padding:6px 14px;border-radius:8px;background:var(--bg-secondary);color:var(--text-primary);font-size:13px;font-weight:500;text-decoration:none;border:1px solid var(--border);';
      // Label: use mod.label if it's a localized object, else mod.name
      const labelMap = mod.label as Record<string, string> | undefined;
      a.textContent = labelMap ? labelMap[locale] || labelMap['en'] || mod.id : mod.id;
      row.appendChild(a);
    });
    offlineContent.appendChild(row);
  }

  // Retry button only when the device IS online (service error, not network)
  if (isOnline) {
    const retryBtn = document.createElement('button');
    retryBtn.style.cssText =
      'padding:8px 24px;border-radius:8px;background:var(--accent);color:var(--accent-text);font-weight:600;font-size:14px;border:none;cursor:pointer;';
    retryBtn.textContent = t('common_retry');
    retryBtn.addEventListener('click', () => {
      if (_activeModuleId) setActiveModule(_activeModuleId, ctx);
    });
    offlineContent.appendChild(retryBtn);
  }

  _offlineEl.style.display = 'flex';
}

function _hideOffline(): void {
  if (_offlineEl) _offlineEl.style.display = 'none';
}

let _blockEl: HTMLElement | null = null;

function _showBlockScreen(
  ctx: AweniContext,
  reason: 'module-blocked' | 'time-expired' | 'outside-hours',
): void {
  if (!_viewport) return;
  _hideAllModules();

  if (!_blockEl) {
    _blockEl = document.createElement('div');
    _blockEl.style.cssText = `
      position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
      background: var(--bg-primary); z-index: 10; padding: 24px;
    `;
    _viewport.appendChild(_blockEl);
  }

  _blockEl.style.display = 'flex';

  const t = ctx.i18n.t.bind(ctx.i18n);
  let icon = '';
  let title = '';
  let message = '';

  if (reason === 'module-blocked') {
    icon = `<svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><line x1="9" y1="9" x2="15" y2="15"/><line x1="15" y1="9" x2="9" y2="15"/></svg>`;
    title = t('parental_module_blocked_title');
    message = t('parental_module_blocked_msg');
  } else if (reason === 'time-expired') {
    icon = `<svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/><path d="M4.93 4.93l14.14 14.14" stroke="#EF4444" stroke-width="2"/></svg>`;
    title = t('parental_time_up_title');
    message = t('parental_time_up_msg');
  } else if (reason === 'outside-hours') {
    const parentalService = getParentalControlService();
    const startHour = parentalService?.controls.allowed_hours_start ?? 7;
    icon = `<svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#6366F1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>`;
    title = t('parental_sleeping_title');
    message = t('parental_sleeping_msg').replace(
      '{time}',
      `${String(startHour).padStart(2, '0')}:00`,
    );
  }

  _blockEl.innerHTML = `
    <div style="text-align:center;max-width:300px;">
      <div style="margin-bottom:16px;">${icon}</div>
      <h2 style="font-size:1.25rem;font-weight:700;color:var(--text-primary);margin-bottom:8px;">${title}</h2>
      <p style="font-size:0.9rem;color:var(--text-secondary);line-height:1.5;">${message}</p>
      <button style="margin-top:20px;padding:10px 24px;border-radius:12px;background:var(--accent);color:var(--accent-text);font-weight:600;font-size:0.9rem;border:none;cursor:pointer;" onclick="this.closest('[style]').style.display='none'">
        ${t('common_close')}
      </button>
    </div>
  `;
}

function _hideAllModules(): void {
  for (const [id] of _containers) _hideModule(id);
  for (const [id] of _iframes) _hideModule(id);
  if (_blockEl) _blockEl.style.display = 'none';
}

// ---------- Idle Preloading ----------

/** Preload adjacent/commonly used modules during browser idle time. */
const _preloadedModules = new Set<string>();
const PRELOAD_MODULES = ['home', 'games', 'memoriz', 'balancetab'];

export function scheduleIdlePreload(): void {
  const preloadNext = () => {
    for (const modId of PRELOAD_MODULES) {
      if (_preloadedModules.has(modId) || _containers.has(modId)) continue;
      _preloadedModules.add(modId);
      // Trigger the import to warm the cache, but don't mount
      _importFallback(modId).catch(() => {
        /* silent preload failure */
      });
      break; // one at a time
    }
  };

  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(() => preloadNext(), { timeout: 3000 });
    // Schedule remaining preloads
    setTimeout(() => {
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(
          () => {
            for (const modId of PRELOAD_MODULES) {
              if (!_preloadedModules.has(modId) && !_containers.has(modId)) {
                _preloadedModules.add(modId);
                _importFallback(modId).catch(() => {});
              }
            }
          },
          { timeout: 5000 },
        );
      }
    }, 2000);
  }
}

// ---------- Viewport Styles ----------

function _injectViewportStyles(): void {
  if (document.getElementById('viewport-transition-styles')) return;
  const style = document.createElement('style');
  style.id = 'viewport-transition-styles';
  style.textContent = `
    @keyframes vpSkeletonPulse {
      0%, 100% { opacity: 0.4; }
      50% { opacity: 0.8; }
    }
    .vp-skeleton-pulse {
      animation: vpSkeletonPulse 1.2s ease-in-out infinite;
    }
    .module-container {
      transition: opacity 200ms ease;
    }
  `;
  document.head.appendChild(style);
}

// ---------- Error Rendering ----------
